// src/components/admin/teamTaskbox/widgets/TaskAnalyticsWidget.js
import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, Clock, Users, CheckCircle, AlertTriangle } from 'lucide-react';

const TaskAnalyticsWidget = ({ tasks }) => {
    const [analytics, setAnalytics] = useState({
        totalTasks: 0,
        completionRate: 0,
        avgCompletionTime: 0,
        overdueRate: 0,
        totalEstimatedHours: 0,
        tasksCreatedThisWeek: 0,
        tasksCompletedThisWeek: 0,
        highPriorityPending: 0
    });

    useEffect(() => {
        calculateAnalytics();
    }, [tasks]);

    const calculateAnalytics = () => {
        if (!tasks || tasks.length === 0) {
            setAnalytics({
                totalTasks: 0,
                completionRate: 0,
                avgCompletionTime: 0,
                overdueRate: 0,
                totalEstimatedHours: 0,
                tasksCreatedThisWeek: 0,
                tasksCompletedThisWeek: 0,
                highPriorityPending: 0
            });
            return;
        }

        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Total tasks
        const totalTasks = tasks.length;

        // Completion rate
        const completedTasks = tasks.filter(t => t.status === 'completed').length;
        const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

        // Overdue rate
        const overdueTasks = tasks.filter(t => t.overdue_days > 0).length;
        const overdueRate = totalTasks > 0 ? Math.round((overdueTasks / totalTasks) * 100) : 0;

        // Total estimated hours
        const totalEstimatedHours = tasks.reduce((sum, task) => {
            return sum + (parseFloat(task.estimated_hours) || 0);
        }, 0);

        // Tasks created this week
        const tasksCreatedThisWeek = tasks.filter(t => {
            const createdDate = new Date(t.created_at);
            return createdDate >= oneWeekAgo;
        }).length;

        // Tasks completed this week
        const tasksCompletedThisWeek = tasks.filter(t => {
            if (t.status !== 'completed' || !t.updated_at) return false;
            const completedDate = new Date(t.updated_at);
            return completedDate >= oneWeekAgo;
        }).length;

        // High priority pending
        const highPriorityPending = tasks.filter(t =>
            (t.priority === 'immediate' || t.priority === 'urgent') &&
            t.status !== 'completed'
        ).length;

        setAnalytics({
            totalTasks,
            completionRate,
            avgCompletionTime: 0, // Would need completion timestamps
            overdueRate,
            totalEstimatedHours: Math.round(totalEstimatedHours),
            tasksCreatedThisWeek,
            tasksCompletedThisWeek,
            highPriorityPending
        });
    };

    const statCards = [
        {
            label: 'Completion Rate',
            value: `${analytics.completionRate}%`,
            icon: CheckCircle,
            color: 'text-green-600',
            bgColor: 'bg-green-100 dark:bg-green-900/30',
            trend: analytics.completionRate >= 70 ? 'up' : 'down'
        },
        {
            label: 'Overdue Tasks',
            value: `${analytics.overdueRate}%`,
            icon: AlertTriangle,
            color: 'text-red-600',
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            trend: analytics.overdueRate <= 10 ? 'up' : 'down'
        },
        {
            label: 'Total Hours',
            value: `${analytics.totalEstimatedHours}h`,
            icon: Clock,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30'
        },
        {
            label: 'High Priority',
            value: analytics.highPriorityPending,
            icon: Target,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100 dark:bg-orange-900/30'
        }
    ];

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Task Analytics
                </h3>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {statCards.map((stat, index) => (
                    <div
                        key={index}
                        className={`${stat.bgColor} rounded-lg p-4`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <stat.icon className={`w-5 h-5 ${stat.color}`} />
                            {stat.trend && (
                                <span className={`text-xs ${
                                    stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                    {stat.trend === 'up' ? '↑' : '↓'}
                                </span>
                            )}
                        </div>
                        <div className={`text-2xl font-bold ${stat.color} mb-1`}>
                            {stat.value}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {stat.label}
                        </div>
                    </div>
                ))}
            </div>

            {/* Weekly Summary */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    This Week
                </h4>
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-blue-600">
                                {analytics.tasksCreatedThisWeek}
                            </span>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Created</div>
                            <div className="text-xs text-gray-500">New tasks</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-green-600">
                                {analytics.tasksCompletedThisWeek}
                            </span>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Completed</div>
                            <div className="text-xs text-gray-500">This week</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskAnalyticsWidget;
